import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import type { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Option = {
  label: string;
  value: string;
  subLabel?: string;
};

type Props = {
  options: Option[];
  defaultValue?: string | null;
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  emptyLabel?: string;
  children?:
    | React.ReactNode
    | ((props: {
        label?: Option["label"];
        value?: Option["value"];
        subLabel?: Option["subLabel"];
      }) => React.ReactNode);
  variant?:
    | "outline"
    | "ghost"
    | "link"
    | "default"
    | "destructive"
    | "secondary"
    | null
    | undefined;
  item?: React.ComponentType<{
    label: string;
    value: string;
    subLabel?: string;
  }>;
  disabled?: boolean;
  triggerClassName?: HTMLAttributes<HTMLButtonElement>["className"];
};

export function ComboBox({
  options,
  defaultValue,
  value: _value,
  onChange,
  placeholder: _placeholder,
  emptyLabel,
  children,
  variant = "outline",
  item: OptionComponent,
  disabled,
  triggerClassName,
}: Props) {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState<string | null>(defaultValue ?? null);

  const [popoverWidth, setPopoverWidth] = React.useState(0);

  React.useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(([e]) => {
      const width = e.contentRect.width;
      setPopoverWidth(width);
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, []);

  React.useEffect(() => {
    setValue(_value ?? null);
  }, [_value]);

  const placeholder = _placeholder || t("select_an_option");

  const popoverStyle = {
    width: "auto",
    minWidth: `${popoverWidth}px`,
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant={variant}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between px-2",
            triggerClassName,
            !value && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          {typeof children === "function" ? (
            children({
              label: options.find((option) => option.value === value)?.label,
              value: options.find((option) => option.value === value)?.value,
              subLabel: options.find((option) => option.value === value)
                ?.subLabel,
            })
          ) : (
            <>
              {value
                ? options.find((option) => option.value === value)?.label
                : placeholder}
              {!disabled && (
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={popoverStyle}>
        <Command
          filter={(value, search) => {
            const option = options.find(
              (opt) => opt.value.toLowerCase() === value.toLowerCase(),
            );
            if (!option) return 0;

            const searchLower = search.toLowerCase();
            const labelMatch = option.label.toLowerCase().includes(searchLower);
            const subLabelMatch = option.subLabel
              ?.toLowerCase()
              .includes(searchLower);

            return labelMatch || subLabelMatch ? 1 : 0;
          }}
        >
          <CommandInput placeholder={placeholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyLabel || t("no_results")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    const newValue = option.value;
                    onChange(newValue);
                    setValue(newValue === value ? "" : newValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    size={16}
                    className={cn(
                      value === option.value ? "opacity-100" : "opacity-0",
                      "mr-3",
                    )}
                  />
                  {OptionComponent ? (
                    <OptionComponent
                      label={option.label}
                      value={option.value}
                      subLabel={option.subLabel}
                    />
                  ) : (
                    option.label
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
